import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star } from "lucide-react";

export default function ShopProfile() {
  const { user, checkAuth } = useAuth();
  const [shopName, setShopName] = useState("");
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setShopName(user.shop_name || "");
      setBio(user.bio || "");
      setSpecialties((user.specialties || []).join(", "));
      (async () => {
        try {
          const res = await api.get(`/shops/${user.user_id}/reviews`);
          setReviews(res.data);
        } catch {}
      })();
    }
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      // shop-status-fixes-nov2 (L2): profile-only update. PATCH /auth/profile
      // never touches role/status, so a rejected shop fixing its profile to
      // reapply is not refused (POST /auth/role is the onboarding endpoint).
      await api.patch("/auth/profile", {
        shop_name: shopName,
        bio,
        specialties: specialties.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await checkAuth();
      toast.success("Profile saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && d ? d : "Could not save your profile");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1000px] mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="label-eyebrow mb-3">SHOP PROFILE</div>
        <h1 className="font-heading text-4xl font-black tracking-tighter mb-8">Your garage identity.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 border border-white/10 bg-[#121214] p-6 sm:p-8 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">SHOP NAME</label>
              <input value={shopName} onChange={(e) => setShopName(e.target.value)} className="input-base" data-testid="profile-shop-name" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">BIO</label>
              <textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} className="input-base" data-testid="profile-bio" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">SPECIALTIES (COMMA SEPARATED)</label>
              <input value={specialties} onChange={(e) => setSpecialties(e.target.value)} className="input-base" placeholder="Brakes, Transmission, EVs" data-testid="profile-specialties" />
            </div>
            <button onClick={save} disabled={loading} className="btn-primary" data-testid="profile-save-btn">{loading ? "..." : "Save"}</button>
          </div>

          <div className="lg:col-span-5 border border-white/10 bg-[#121214] p-6">
            <div className="label-eyebrow mb-4">RATING</div>
            <div className="flex items-center gap-3 mb-6">
              <div className="font-heading text-5xl font-black">{user?.rating_avg || "—"}</div>
              <div>
                <div className="flex text-[#FF9500]">
                  {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= Math.round(user?.rating_avg || 0) ? "fill-[#FF9500]" : ""}`} />)}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{user?.rating_count || 0} reviews</div>
              </div>
            </div>
            <div className="label-eyebrow mb-3">RECENT REVIEWS</div>
            {reviews.length === 0 ? (
              <div className="text-sm text-zinc-500">No reviews yet.</div>
            ) : (
              <div className="space-y-3">
                {reviews.slice(0, 5).map((r) => (
                  <div key={r.review_id} className="border border-white/10 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex text-[#FF9500] text-xs">
                        {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "fill-[#FF9500]" : ""}`} />)}
                      </div>
                      <span className="label-eyebrow text-zinc-600">{r.owner_handle}</span>
                    </div>
                    <p className="text-sm text-zinc-400">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
